import { FC } from 'react'

import {
  BankingPaymentRequestFilter,
  PaymentRequestsOverdueWidgetDocument,
  PaymentRequestsOverdueWidgetQuery,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import {
  ChartData,
  FinanceWidget,
  SupportedCharts,
} from 'modules/finance/components/Widgets/common/FinanceWidget'
import { buildWidget } from 'modules/finance/components/Widgets/common/widgets'

interface PreparedDataArgsType {
  amountFormatter: (value: number) => string
  displayedFilters: (string | string[])[]
}

interface QueryArgsType {
  prFilter?: BankingPaymentRequestFilter
}

const prepareData = (
  data: PaymentRequestsOverdueWidgetQuery,
  prepareDataArgs: PreparedDataArgsType
) => {
  const overdueAmount = data?.banking?.overdue?.aggregate?.sum?.amountCents || 0
  const dueThisWeekAmount = data?.banking?.dueThisWeek?.aggregate?.sum?.amountCents || 0
  const onTimeAmount = data?.banking?.onTime?.aggregate?.sum?.amountCents || 0
  const overdueCount = data?.banking?.overdue?.aggregate?.count || 0
  const dueThisWeekCount = data?.banking?.dueThisWeek?.aggregate?.count || 0
  const onTimeCount = data?.banking?.onTime?.aggregate?.count || 0
  const totalAmount = overdueAmount + dueThisWeekAmount + onTimeAmount
  const totalCount = overdueCount + dueThisWeekCount + onTimeCount

  const overviewChartData: ChartData = {
    label: `${i18n.t('finance.overview.payments.total')} (${totalCount})`,
    value: totalAmount,
    formattedValue: prepareDataArgs.amountFormatter(totalAmount),
  }

  const chartDatas: ChartData[] = [
    {
      status: 'onTime',
      color: 'categorical-2',
      label: `${i18n.t('finance.overview.payments.on_time')} (${onTimeCount})`,
      value: onTimeAmount,
      formattedValue: prepareDataArgs.amountFormatter(onTimeAmount),
    },
    {
      status: 'dueThisWeek',
      color: 'categorical-3',
      label: `${i18n.t('finance.overview.payments.due_this_week')} (${dueThisWeekCount})`,
      value: dueThisWeekAmount,
      formattedValue: prepareDataArgs.amountFormatter(dueThisWeekAmount),
    },
    {
      status: 'overdue',
      color: 'feedback-negative',
      label: `${i18n.t('finance.overview.payments.overdue')} (${overdueCount})`,
      value: overdueAmount,
      formattedValue: prepareDataArgs.amountFormatter(overdueAmount),
    },
  ]

  return {
    overviewChartData,
    chartDatas,
    amountFormatter: prepareDataArgs.amountFormatter,
    displayedFilters: prepareDataArgs.displayedFilters,
  }
}

export const buildPaymentRequestsOverdueDashboardWidget = ({
  queryArgs,
  prepareDataArgs,
}: {
  queryArgs: QueryArgsType
  prepareDataArgs: PreparedDataArgsType
}) => {
  const PaymentRequestsOverdueDashboardWidget: FC<{
    data: ReturnType<typeof prepareData>
  }> = ({ data }) => {
    const { navigation } = useNavigation()
    const { overviewChartData, chartDatas, amountFormatter, displayedFilters } = data

    return (
      <FinanceWidget
        id="paymentRequestsOverdueDashboard"
        title={i18n.t('finance.overview.payment_requests.payment_requests_by_due_date')}
        displayedFilters={displayedFilters}
        tickFormatter={(value) => amountFormatter(Number(value))}
        type={SupportedCharts.PIE}
        showChart
        chartDatas={chartDatas}
        overviewChartData={overviewChartData}
        linkData={{
          url: `${navigation.finance.treasury.banking.paymentRequests.fullPath()}?status=init`,
          title: i18n.t('finance.overview.payment_requests.title'),
        }}
        emptyState={{
          icon: '💰',
          text: i18n.t('finance.overview.payment_requests.empty_state.title'),
          description: i18n.t('finance.overview.payment_requests.empty_state.description'),
        }}
      />
    )
  }

  return buildWidget({
    id: 'paymentRequestsOverdueWidget',
    component: PaymentRequestsOverdueDashboardWidget,
    document: PaymentRequestsOverdueWidgetDocument,
    prepareData: (data: PaymentRequestsOverdueWidgetQuery) => prepareData(data, prepareDataArgs),
    queryArgs: {
      ...queryArgs,
    },
  })
}
